"use client";

import { useCallback, useState } from "react";
import { WITNESS_CARD_IMAGE_SRCS } from "@/components/ui/Cwitness";
import { useAuth } from "@/contexts/auth";
import { useCurrentUser } from "@/contexts/current-user";
import { useStartCheckout, useSubscription } from "@/contexts/subscription";
import { useViewport } from "@/hooks/dom";
import { QUERY_STATUS } from "@/types/async-protocol";
import type { HomePageModalStateLocal } from "./domain/home-page/types";
import {
  getAuthUser,
  getIsAuthenticated,
  getUserId,
} from "./homePageAuthSelectors";
import { getHomePageNavbarState } from "./homePageNavbarSelectors";
import {
  getHasProfileError,
  getProfileImageSrc,
} from "./homePageProfileSelectors";
import { getHasActiveSubscription } from "./homePageSubscriptionSelectors";
import {
  useHomePageTelemetry,
} from "./useHomePageTelemetry";
import { useHomePageModalState } from "./useHomePageModalState";
import { useImagePreload } from "./useImagePreload";

type HomePageTelemetryLocal = ReturnType<typeof useHomePageTelemetry>;
type HomePageNavbarStateLocal = ReturnType<typeof getHomePageNavbarState>;

interface HomePageRuntimeLocal {
  clickInvestigationLog: HomePageTelemetryLocal["clickInvestigationLog"];
  hasActiveSubscription: boolean;
  hasProfileError: boolean;
  isAuthenticated: boolean;
  landingPerfPrepageLog: HomePageTelemetryLocal["landingPerfPrepageLog"];
  loading: boolean;
  modalDebugLogger: HomePageTelemetryLocal["modalDebugLogger"];
  modalState: HomePageModalStateLocal;
  navbarState: HomePageNavbarStateLocal;
  openUserMenu: () => void;
  openUserMenuRequestId: number | undefined;
  profileImageSrc: string;
  startCheckout: ReturnType<typeof useStartCheckout>;
  userId: string | null;
  viewport: string | null;
  viewportLoaded: boolean;
}

export function useHomePageRuntime(): HomePageRuntimeLocal {
  useImagePreload({ srcs: WITNESS_CARD_IMAGE_SRCS });
  const { clickInvestigationLog, landingPerfPrepageLog, modalDebugLogger } =
    useHomePageTelemetry();
  const startCheckout = useStartCheckout();
  const subscriptionQuery = useSubscription();
  const auth = useAuth();
  const currentUser = useCurrentUser();
  const { viewport, loaded: viewportLoaded } = useViewport();
  const modalState = useHomePageModalState();
  const [openUserMenuRequestId, setOpenUserMenuRequestId] = useState<
    number | undefined
  >(undefined);
  const user = getAuthUser(auth.data);
  const isAuthenticated = getIsAuthenticated(user);
  const loading = auth.status === QUERY_STATUS.Loading;
  const profileData = currentUser.data;
  const profileLoading = currentUser.status === QUERY_STATUS.Loading;
  const openUserMenu = useCallback(() => {
    setOpenUserMenuRequestId((previous) => (previous ?? 0) + 1);
  }, [setOpenUserMenuRequestId]);

  return {
    clickInvestigationLog,
    hasActiveSubscription: getHasActiveSubscription(subscriptionQuery.data),
    hasProfileError: getHasProfileError(currentUser.error),
    isAuthenticated,
    landingPerfPrepageLog,
    loading,
    modalDebugLogger,
    modalState,
    navbarState: getHomePageNavbarState({
      isAuthenticated,
      loading,
      profileLoading,
    }),
    openUserMenu,
    openUserMenuRequestId,
    profileImageSrc: getProfileImageSrc(profileData),
    startCheckout,
    userId: getUserId(user),
    viewport: viewport ?? null,
    viewportLoaded,
  };
}
